// File: frontend/src/pages/exams/ExamList.jsx

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/axios';

const ExamList = () => {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState(null);

  // New Exam Form State
  const [name, setName] = useState('');
  const [classId, setClassId] = useState('1');
  const [term, setTerm] = useState('First Term');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState('');
  const [status, setStatus] = useState('scheduled');

  useEffect(() => {
    fetchExams();
  }, []);

  /**
   * Fetch all exams from backend API
   */
  const fetchExams = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/v1/exams');
      if (response.data?.success) {
        setExams(response.data.data || []);
      } else {
        setError(response.data?.message || 'Failed to load exams list.');
      }
    } catch (err) {
      console.error('Error fetching exams:', err);
      setError(err.response?.data?.message || err.message || 'Error fetching exams.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Submit new exam creation form
   */
  const handleCreateExam = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setModalError('Exam name is required');
      return;
    }

    try {
      setSubmitting(true);
      setModalError(null);

      const payload = {
        name: name.trim(),
        class_id: classId,
        term: term.trim(),
        start_date: startDate,
        end_date: endDate || null,
        status
      };

      const response = await api.post('/api/v1/exams', payload);

      if (response.data?.success) {
        setShowModal(false);
        setName('');
        fetchExams();
      } else {
        setModalError(response.data?.message || 'Failed to create exam.');
      }
    } catch (err) {
      console.error('Error creating exam:', err);
      setModalError(err.response?.data?.message || err.message || 'Failed to create exam.');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Get status badge styling
   */
  const getStatusBadge = (examStatus) => {
    switch (examStatus?.toLowerCase()) {
      case 'ongoing':
        return 'bg-emerald-100 text-emerald-800 border border-emerald-300';
      case 'completed':
        return 'bg-slate-100 text-slate-700 border border-slate-300';
      case 'scheduled':
      default:
        return 'bg-blue-100 text-blue-800 border border-blue-300';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Navbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard/admin" className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 flex items-center justify-center font-bold transition">
              ←
            </Link>
            <div>
              <h1 className="font-bold text-slate-800 text-lg leading-tight">Exams & Examinations</h1>
              <p className="text-xs text-slate-500">Manage term examinations, classes, and results</p>
            </div>
          </div>

          <button
            onClick={() => setShowModal(true)}
            className="bg-primary hover:bg-blue-600 text-white font-semibold px-4 py-2.5 rounded-xl text-sm shadow-sm transition flex items-center gap-2"
          >
            <span className="text-base">+</span> Schedule New Exam
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-800 rounded-xl text-sm flex items-center justify-between shadow-sm">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="font-bold text-red-600">✕</button>
          </div>
        )}

        {/* Exams Table Card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-bold text-slate-800 text-base">Examination Schedule</h2>
            <span className="text-xs text-slate-500">Total Exams: {exams.length}</span>
          </div>

          {loading ? (
            <div className="p-12 text-center text-slate-400 font-medium">Loading examination schedules...</div>
          ) : exams.length === 0 ? (
            <div className="p-12 text-center text-slate-400 font-medium">
              No examinations scheduled yet. Click "Schedule New Exam" above to add one.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs font-sans">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 uppercase font-semibold">
                    <th className="py-3.5 px-6">Exam Name</th>
                    <th className="py-3.5 px-4">Class</th>
                    <th className="py-3.5 px-4">Term</th>
                    <th className="py-3.5 px-4">Start Date</th>
                    <th className="py-3.5 px-4">Status</th>
                    <th className="py-3.5 px-6 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700 font-medium">
                  {exams.map((exam) => (
                    <tr key={exam.id} className="hover:bg-slate-50/80 transition">
                      <td className="py-4 px-6 font-bold text-slate-900 text-sm">{exam.name}</td>
                      <td className="py-4 px-4 font-semibold text-slate-700">
                        {exam.class_name || `Class ${exam.class_id}`}
                      </td>
                      <td className="py-4 px-4">{exam.term}</td>
                      <td className="py-4 px-4 font-mono text-slate-600">
                        {exam.start_date ? new Date(exam.start_date).toLocaleDateString() : '-'}
                      </td>
                      <td className="py-4 px-4">
                        <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold capitalize ${getStatusBadge(exam.status)}`}>
                          {exam.status}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-right space-x-2">
                        <Link
                          to={`/exams/enter/${exam.id}`}
                          className="bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-semibold px-3 py-1.5 rounded-lg border border-emerald-200 transition text-xs inline-block"
                        >
                          Enter Results
                        </Link>
                        <Link
                          to={`/exams/results/${exam.id}`}
                          className="bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold px-3 py-1.5 rounded-lg transition text-xs inline-block"
                        >
                          View Results
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Schedule Exam Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-200 animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between border-b pb-4 mb-4">
              <h3 className="font-bold text-slate-800 text-base">Schedule New Examination</h3>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600 font-bold">✕</button>
            </div>

            {modalError && (
              <div className="mb-4 p-3 bg-red-50 text-red-700 border border-red-200 rounded-xl text-xs">
                {modalError}
              </div>
            )}

            <form onSubmit={handleCreateExam} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">Exam Name *</label>
                <input
                  type="text"
                  placeholder="e.g. Mid-Term Examination 2026"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-primary focus:bg-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">Class *</label>
                  <select
                    value={classId}
                    onChange={(e) => setClassId(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((c) => (
                      <option key={c} value={c}>Class {c}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">Term *</label>
                  <select
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                  >
                    <option value="First Term">First Term</option>
                    <option value="Second Term">Second Term</option>
                    <option value="Final Term">Final Term</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">Start Date *</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    required
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs capitalize"
                >
                  <option value="scheduled">Scheduled</option>
                  <option value="ongoing">Ongoing</option>
                  <option value="completed">Completed</option>
                </select>
              </div>

              <div className="pt-3 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl text-xs transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 py-2.5 bg-primary hover:bg-blue-600 text-white font-semibold rounded-xl text-xs transition disabled:opacity-50"
                >
                  {submitting ? 'Saving...' : 'Save Examination'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default ExamList;
